import { lazy } from 'react'
import { Trans } from 'react-i18next'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { IActionFormProps, isEmail } from '@makyoapp/makyo-common'
import { Button, ButtonVariant, Field, Label } from '@makyoapp/makyo-ui'

import { useForm } from '@/lib/use-form'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { isEmpty } from '@/shared/utils'
import { ActionInputV3 } from '@/components/ActionInputV3/ActionInputV3'

import { TableSelect } from '../../shared/TableSelect'

export default function ActFormComposeEmail({ onSubmit, onDismiss, initialData, triggerSource }: IActionFormProps) {
  const { tables } = useDbClientContext()

  // create table options
  const tblOptions = tables
  const shouldShowTable = ['menu-item'].includes(triggerSource)
  const defaultTableId = shouldShowTable ? (!!tblOptions.length ? tblOptions[0]?.id : '') : undefined

  const { form, onSubmit: onFormSubmit } = useForm({
    defaultValues: {
      tableId: initialData?.tableId ?? defaultTableId ?? '',
      email: initialData?.email || {},
      cc: initialData?.cc || {},
      bcc: initialData?.bcc || {},
      subject: initialData?.subject || {},
      message: initialData?.message || {},
    },
    validate(data, setError) {
      let isManualInput = !data.email.tableId && !data.email.dataType
      let emailValue = data.email.value

      let emailError = !emailValue?.trim().length
        ? 'Email cannot be empty'
        : isManualInput && !isEmail(data.email.value)
          ? 'Please enter a valid email'
          : null

      setError('email', emailError)
    },
  })

  const handleSubmit = onFormSubmit((data) => {
    onSubmit(data)
  })

  const email = form.getValues('email')
  const tableId = form.getValues('tableId')
  const invalidForm = isEmpty(email) || !!form.errors || (shouldShowTable && isEmpty(tableId))
  return (
    <div>
      <div className='p-4'>
        {shouldShowTable && (
          <Field className='min-h-0'>
            <Label required>Table</Label>

            <TableSelect
              options={tblOptions}
              value={tblOptions.find((tb) => tb.id === form.getValues('tableId'))}
              onChange={(tb) => {
                form.setValues('tableId', tb.id)
              }}
              error={form.errors?.tableId}
            />
          </Field>
        )}

        <Field>
          <Label required>To</Label>
          <ActionInputV3
            options={initialData?.actionOptions}
            isActionWorkflow={initialData?.isActionWorkflow}
            globalTableId={shouldShowTable ? form.getValues('tableId') : undefined}
            placeholder='Enter email'
            value={form.getValues('email')}
            columnTypes={[ColumnTypes.Email]}
            onChange={({ ...value }) => {
              form.setValues('email', value)
            }}
            error={form.errors?.email}
          />
        </Field>

        <Field>
          <Label>CC</Label>
          <ActionInputV3
            options={initialData?.actionOptions}
            isActionWorkflow={initialData?.isActionWorkflow}
            globalTableId={shouldShowTable ? form.getValues('tableId') : undefined}
            placeholder='Enter CC'
            value={form.getValues('cc')}
            columnTypes={[ColumnTypes.Email]}
            onChange={({ ...value }) => {
              form.setValues('cc', value)
            }}
            error={form.errors?.cc}
          />
        </Field>

        <Field>
          <Label>BCC</Label>
          <ActionInputV3
            options={initialData?.actionOptions}
            isActionWorkflow={initialData?.isActionWorkflow}
            globalTableId={shouldShowTable ? form.getValues('tableId') : undefined}
            placeholder='Enter BCC'
            value={form.getValues('bcc')}
            columnTypes={[ColumnTypes.Email]}
            onChange={({ ...value }) => {
              form.setValues('bcc', value)
            }}
            error={form.errors?.bcc}
          />
        </Field>

        <Field>
          <Label>Subject</Label>
          <ActionInputV3
            options={initialData?.actionOptions}
            isActionWorkflow={initialData?.isActionWorkflow}
            globalTableId={shouldShowTable ? form.getValues('tableId') : undefined}
            value={form.getValues('subject')}
            columnTypes={[ColumnTypes.Text]}
            onChange={({ ...value }) => {
              form.setValues('subject', value)
            }}
            error={form.errors?.subject}
          />
        </Field>

        <Field>
          <Label>Message</Label>
          <ActionInputV3
            options={initialData?.actionOptions}
            isActionWorkflow={initialData?.isActionWorkflow}
            globalTableId={shouldShowTable ? form.getValues('tableId') : undefined}
            placeholder='Message goes here'
            inputType='textarea'
            value={form.getValues('message')}
            columnTypes={[ColumnTypes.Text, ColumnTypes.RichText, ColumnTypes.Join]}
            onChange={({ ...value }) => {
              form.setValues('message', value)
            }}
            error={form.errors?.message}
          />
        </Field>
      </div>

      <div className='px-4 py-2 border-t bdr flex justify-between items-center'>
        <Button variant={ButtonVariant.Outlined} onClick={onDismiss}>
          <Trans>Cancel</Trans>
        </Button>
        <Button onClick={handleSubmit} disabled={invalidForm}>
          <Trans>Save</Trans>
        </Button>
      </div>
    </div>
  )
}
